/**
 * Lending route group.
 *
 * Loans, mortgages and card operations routes.
 *
 * Extracted verbatim from AppRoutes.tsx. The declaration sequence is preserved
 * exactly as part of the route structural contract; see
 * `__tests__/AppRoutes.structure.test.tsx`.
 */

import { lazy } from 'react';
import { Route } from 'react-router-dom';
import { ProtectedRoute } from '../../auth/components/ProtectedRoute';
import { ROUTES, permissionsFor } from '../routeCatalogue';

// ---------------------------------------------------------------------------
// Lazy-loaded route pages
// ---------------------------------------------------------------------------

const LoansDashboard = lazy(() => import('../../features/loans/pages/LoansDashboard'));
const ApplicationsPipeline = lazy(() => import('../../features/loans/pages/ApplicationsPipeline'));
const ApplicationWizard = lazy(() => import('../../features/loans/pages/ApplicationWizard'));
const ApplicationWorkspace = lazy(() => import('../../features/loans/pages/ApplicationWorkspace'));
const LoanPipeline = lazy(() => import('../../features/loans/pages/LoanPipeline'));
const LoanApplicationDetail = lazy(() => import('../../features/loans/pages/LoanApplicationDetail'));
const CreditDecisioning = lazy(() => import('../../features/loans/pages/CreditDecisioning'));
const DisbursementPage = lazy(() => import('../../features/loans/pages/Disbursement'));
const PortfolioRisk = lazy(() => import('../../features/loans/pages/PortfolioRisk'));
const AccountsPage = lazy(() => import('../../features/loans/pages/AccountsPage'));
const AccountWorkspace = lazy(() => import('../../features/loans/pages/AccountWorkspace').then(m => ({ default: m.AccountWorkspace })));
const CollectionsDashboard = lazy(() => import('../../features/loans/pages/CollectionsDashboard').then(m => ({ default: m.CollectionsDashboard })));
const CollectionsWorkspace = lazy(() => import('../../features/loans/pages/CollectionsWorkspace'));
const AdminLoanPipeline = lazy(() => import('../../pages/loans/LoanPipeline'));
const AdminLoanApplicationDetail = lazy(() => import('../../pages/loans/LoanApplicationDetail'));
const AdminLoanProducts = lazy(() => import('../../pages/loans/LoanProducts'));
const AdminLoanPortfolio = lazy(() => import('../../pages/loans/LoanPortfolio'));
const AdminLoanPortfolioDetail = lazy(() => import('../../pages/loans/LoanPortfolioDetail'));
const AdminLoanCollections = lazy(() => import('../../pages/loans/LoanCollections'));
const AdminMortgagePipeline = lazy(() => import('../../pages/mortgages/MortgagePipeline'));
const AdminMortgageProducts = lazy(() => import('../../pages/mortgages/MortgageProducts'));
const AdminMortgagePortfolio = lazy(() => import('../../pages/mortgages/MortgagePortfolio'));
const AdminMortgageDetail = lazy(() => import('../../pages/mortgages/MortgageDetail'));
const AdminCardList = lazy(() => import('../../pages/cards/CardList'));
const AdminCardDetail = lazy(() => import('../../pages/cards/CardDetail'));
const AdminCardDisputes = lazy(() => import('../../pages/cards/CardDisputes'));
const ProductsPage = lazy(() => import('../../features/loans/pages/ProductsPage'));
const LoansReportsPage = lazy(() => import('../../features/loans/pages/ReportsPage'));
const MortgagePipeline = lazy(() => import('../../features/mortgages/pages/MortgagePipeline'));
const MortgageApplicationDetail = lazy(() => import('../../features/mortgages/pages/MortgageApplicationDetail'));
const MortgageProducts = lazy(() => import('../../features/mortgages/pages/MortgageProducts'));
const AffordabilityView = lazy(() => import('../../features/mortgages/pages/AffordabilityView'));
const MortgageReports = lazy(() => import('../../features/mortgages/pages/MortgageReports'));

// ---------------------------------------------------------------------------
// lendingRoutes()
// ---------------------------------------------------------------------------

export function lendingRoutes() {
    return (
        <>
        {/* Loans Routes */}
        <Route path={ROUTES.LOANS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS)}>
                <LoansDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_PIPELINE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_PIPELINE)}>
                <AdminLoanPipeline />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_APPLICATIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_APPLICATIONS)}>
                <ApplicationsPipeline />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_APPLICATIONS_NEW.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_APPLICATIONS_NEW)}>
                <ApplicationWizard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_APPLICATIONS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_APPLICATIONS_DETAIL)}>
                <AdminLoanApplicationDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_APPLICATIONS_DETAIL_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_APPLICATIONS_DETAIL_DETAIL)}>
                <LoanApplicationDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_CREDIT_DECISIONING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_CREDIT_DECISIONING)}>
                <CreditDecisioning />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_DISBURSEMENT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_DISBURSEMENT)}>
                <DisbursementPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_PORTFOLIO.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_PORTFOLIO)}>
                <AdminLoanPortfolio />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_PORTFOLIO_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_PORTFOLIO_DETAIL)}>
                <AdminLoanPortfolioDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_ACCOUNTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_ACCOUNTS)}>
                <AccountsPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_ACCOUNTS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_ACCOUNTS_DETAIL)}>
                <AccountWorkspace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_COLLECTIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_COLLECTIONS)}>
                <AdminLoanCollections />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_COLLECTIONS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_COLLECTIONS_DETAIL)}>
                <CollectionsWorkspace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_PRODUCTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_PRODUCTS)}>
                <AdminLoanProducts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LOANS_REPORTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LOANS_REPORTS)}>
                <LoansReportsPage />
            </ProtectedRoute>
        } />

        {/* Mortgage Routes */}
        <Route path={ROUTES.MORTGAGES_PIPELINE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_PIPELINE)}>
                <AdminMortgagePipeline />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.MORTGAGES_APPLICATIONS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_APPLICATIONS_DETAIL)}>
                <MortgageApplicationDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.MORTGAGES_PRODUCTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_PRODUCTS)}>
                <AdminMortgageProducts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.MORTGAGES_PORTFOLIO.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_PORTFOLIO)}>
                <AdminMortgagePortfolio />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.MORTGAGES_PORTFOLIO_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_PORTFOLIO_DETAIL)}>
                <AdminMortgageDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.MORTGAGES_AFFORDABILITY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_AFFORDABILITY)}>
                <AffordabilityView />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.MORTGAGES_REPORTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.MORTGAGES_REPORTS)}>
                <MortgageReports />
            </ProtectedRoute>
        } />

        {/* Card Operations Routes (Phase 9C.6) */}
        <Route path={ROUTES.CARDS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.CARDS)}>
                <AdminCardList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.CARDS_DISPUTES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.CARDS_DISPUTES)}>
                <AdminCardDisputes />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.CARDS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.CARDS_DETAIL)}>
                <AdminCardDetail />
            </ProtectedRoute>
        } />
        </>
    );
}
